import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from 'qampo-ui-library';
import { Button } from '../UI/base/Button';
import type { InterventionGroupDto, TreatmentPlanDto } from '../../api/generated';
import { useRemovalPreview } from '../../hooks/useRemovalPreview';
import { AffectedGroupsPicker } from './AffectedGroupsPicker';
import { CompletedGroupsWarning } from './CompletedGroupsWarning';
import { computeAffectedGroupIds, DEFAULT_AFFECTED_GROUPS_CHOICE } from './affectedGroups';
import styles from './AffectedGroupsPopover.module.css';

interface AffectedGroupsPopoverProps {
  anchorRef: RefObject<HTMLButtonElement | null>;
  plan: TreatmentPlanDto;
  selectedGroup: InterventionGroupDto;
  title: string;
  confirmLabel: string;
  // Identify the pending removal. Without all four, nothing can be fetched and
  // confirm stays closed.
  locationId?: string;
  scenarioId?: string;
  patientId?: string;
  onClose: () => void;
  onConfirm: (groupIds: string[]) => void;
}

const POPOVER_GAP_PX = 6;
const VIEWPORT_PADDING_PX = 8;

export const AffectedGroupsPopover = ({
  anchorRef,
  plan,
  selectedGroup,
  title,
  confirmLabel,
  locationId,
  scenarioId,
  patientId,
  onClose,
  onConfirm
}: AffectedGroupsPopoverProps) => {
  const { t } = useTranslation();
  const popoverRef = useRef<HTMLDivElement>(null);
  const [choice, setChoice] = useState(DEFAULT_AFFECTED_GROUPS_CHOICE);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  // Which set of completed groups was acknowledged, not merely that something was.
  // Widening the scope brings more economy into range, so the tick has to stop
  // counting: it was given for a smaller loss than the one now pending. Derived
  // rather than reset in an effect, which this repo's lint rules forbid.
  //
  // Never leaves this component. The server no longer asks whether the loss was
  // accepted, so declining simply means no remove request is sent at all.
  const [acknowledgedGroups, setAcknowledgedGroups] = useState<string | null>(null);
  const groupIds = computeAffectedGroupIds(plan, selectedGroup, choice);

  const { completedGroups, isLoading, hasError } = useRemovalPreview({
    locationId,
    scenarioId,
    treatmentPlanId: plan.id,
    patientId
  });

  // The preview covers the whole plan, so narrowing it to the chosen scope is local
  // and the warning follows the picker without another request.
  const completedInScope = completedGroups.filter((group) =>
    groupIds.includes(group.interventionGroupId)
  );

  const completedInScopeKey = completedInScope.map((group) => group.interventionGroupId).join(',');
  const isAcknowledged = acknowledgedGroups === completedInScopeKey;

  // Fail closed: a preview that did not arrive is not evidence that there is
  // nothing to lose, and it is the only thing standing between the user and a
  // silent discard now that the server does not second-guess the removal.
  const canConfirm =
    groupIds.length > 0 &&
    !isLoading &&
    !hasError &&
    (completedInScope.length === 0 || isAcknowledged);

  const computePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const popover = popoverRef.current;
    if (!anchor || !popover) return;
    const anchorRect = anchor.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    let top = anchorRect.bottom + POPOVER_GAP_PX;
    if (top + popoverRect.height > window.innerHeight - VIEWPORT_PADDING_PX) {
      top = anchorRect.top - popoverRect.height - POPOVER_GAP_PX;
    }
    let left = anchorRect.right - popoverRect.width;
    if (left < VIEWPORT_PADDING_PX) left = VIEWPORT_PADDING_PX;
    if (left + popoverRect.width > window.innerWidth - VIEWPORT_PADDING_PX) {
      left = window.innerWidth - popoverRect.width - VIEWPORT_PADDING_PX;
    }
    setPosition({ top, left });
  }, [anchorRef]);

  useEffect(() => {
    computePosition();
    window.addEventListener('resize', computePosition);
    return () => window.removeEventListener('resize', computePosition);
  }, [computePosition]);

  useClickOutside(popoverRef, (event) => {
    const target = event?.target;
    if (target instanceof Node && anchorRef.current?.contains(target)) return;
    onClose();
  });

  useEffect(() => {
    // Capture-phase + stopPropagation so the drawer's document-level Escape
    // listener doesn't also fire and close the drawer behind this popover.
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [onClose]);

  return createPortal(
    <div
      ref={popoverRef}
      className={styles.popover}
      style={
        position
          ? { top: position.top, left: position.left }
          : { visibility: 'hidden' }
      }
      role="dialog"
    >
      <h4 className={styles.title}>{title}</h4>
      <AffectedGroupsPicker
        plan={plan}
        selectedGroup={selectedGroup}
        choice={choice}
        onChange={setChoice}
      />
      <CompletedGroupsWarning
        completedGroups={completedInScope}
        isAcknowledged={isAcknowledged}
        onAcknowledgedChange={(checked) =>
          setAcknowledgedGroups(checked ? completedInScopeKey : null)
        }
      />
      <div className={styles.footer}>
        <Button type="secondary" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button
          type="primary"
          onClick={() => onConfirm(groupIds)}
          disabled={!canConfirm}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>,
    document.body
  );
};
